import React, { useState, useMemo, useEffect } from 'react';
import { Filter } from 'lucide-react';
import {
  TrendLineChart,
  ComparisonBarChart,
  RankingTable,
  RegionHeatmap,
} from '../../../components/charts';
import { useGetTopPerformingSummaryQuery } from '../../../services/attendanceApi';
import type { RegionTrendData, RankingEntry } from '../../../types/analytics';
import {
  regionTrendsToRankings,
  transformSummaryToRegionTrends,
  type ComputedDateRange,
} from '../useAnalyticsData';

interface RegionsTabProps {
  computedDateRange: ComputedDateRange;
  heatmapData: any[];
  employeeApiType: 'regular' | 'contractual';
}

const RegionsTab: React.FC<RegionsTabProps> = ({
  computedDateRange,
  heatmapData,
  employeeApiType,
}) => {
  // ── Tab-specific RTK query (only fires when this component mounts) ──
  const { data: allRegionSummaryData } = useGetTopPerformingSummaryQuery(
    {
      fromDate: computedDateRange.fromDateStr,
      toDate: computedDateRange.toDateStr,
      flag: 'ro',
      limit: 100,
      order: 'DESC',
    },
    { skip: !computedDateRange.isValid },
  );

  // ── Derived data ──
  const allRegionTrends = useMemo(
    (): RegionTrendData[] => {
      const trends = transformSummaryToRegionTrends(allRegionSummaryData?.data, employeeApiType);
      trends.sort((a, b) => b.averageAttendance - a.averageAttendance);
      trends.forEach((t, i) => (t.rank = i + 1));
      return trends;
    },
    [allRegionSummaryData, employeeApiType],
  );

  // ── Region selection state ──
  const [selectedRegions, setSelectedRegions] = useState<number[]>([]);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  // Auto-select top 5 on first data load
  useEffect(() => {
    if (allRegionTrends.length > 0 && !hasAutoSelected) {
      setSelectedRegions(allRegionTrends.slice(0, 5).map(r => r.roId));
      setHasAutoSelected(true);
    }
  }, [allRegionTrends, hasAutoSelected]);

  const filteredRegionTrends = useMemo(
    () => allRegionTrends.filter(r => selectedRegions.includes(r.roId)),
    [allRegionTrends, selectedRegions],
  );

  const selectedRegionNames = allRegionTrends
    .filter(r => selectedRegions.includes(r.roId))
    .map(r => r.roName);

  const heatmapDisplayData = useMemo(
    () =>
      heatmapData.map(d => ({
        ...d,
        value: employeeApiType === 'contractual' ? d.contractualPercent : d.regularPercent,
      })),
    [heatmapData, employeeApiType],
  );

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Region Selection Dropdown */}
          <div className="lg:col-span-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Filter className="w-4 h-4 text-gray-500" />
              Select Regions to Compare (up to 8):
            </label>
            <div className="relative">
              <div className="max-h-72 overflow-y-auto border border-gray-300 rounded-lg">
                {allRegionTrends.map(region => (
                  <label
                    key={region.roId}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRegions.includes(region.roId)}
                      onChange={e => {
                        if (e.target.checked) {
                          if (selectedRegions.length < 8) {
                            setSelectedRegions([...selectedRegions, region.roId]);
                          } else {
                            alert('You can select up to 8 regions only');
                          }
                        } else {
                          setSelectedRegions(selectedRegions.filter(id => id !== region.roId));
                        }
                      }}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="flex-1 text-sm text-gray-700">{region.roName}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {region.averageAttendance.toFixed(1)}%
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Select up to 8 regions to compare. If none selected, top 5 regions will be shown.
              </p>
            </div>
            {selectedRegionNames.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs font-medium text-gray-600">Selected:</span>
                {selectedRegionNames.map((name, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full"
                  >
                    {name}
                    <button
                      onClick={() => {
                        const regionToRemove = allRegionTrends.find(r => r.roName === name);
                        if (regionToRemove) {
                          setSelectedRegions(selectedRegions.filter(id => id !== regionToRemove.roId));
                        }
                      }}
                      className="ml-1 hover:text-purple-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  onClick={() => setSelectedRegions([])}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>

          {/* Quick Select Buttons */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Quick Select:</label>
            <div className="space-y-2">
              <button
                onClick={() => setSelectedRegions(allRegionTrends.slice(0, 5).map(r => r.roId))}
                className="w-full px-3 py-2 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                Top 5 Regions
              </button>
              <button
                onClick={() => setSelectedRegions(allRegionTrends.slice(-5).map(r => r.roId))}
                className="w-full px-3 py-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                Bottom 5 Regions
              </button>
            </div>
            <div className="mt-3 p-2 bg-purple-50 rounded-lg">
              <span className="text-xs text-gray-500">Showing:</span>
              <span className="text-sm font-medium text-purple-600 ml-1">
                {employeeApiType === 'regular' ? 'Regular Staff' : 'Contractual Staff'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Region Attendance Trend Comparison
          {selectedRegions.length > 0
            ? ` (${selectedRegions.length} regions selected)`
            : ' (Top 5 regions)'}
        </h3>
        <TrendLineChart
          data={filteredRegionTrends}
          height={400}
          showLegend={true}
          showArea={false}
          compareMode={employeeApiType}
        />
      </div>

      {/* Heatmap */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Attendance by Region</h3>
          <span className="text-sm text-purple-600 font-medium">
            {employeeApiType === 'regular' ? 'Regular Staff' : 'Contractual Staff'}
          </span>
        </div>
        <RegionHeatmap data={heatmapDisplayData} height={500} showLabels={true} />
      </div>

      {/* Bar Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <ComparisonBarChart
            data={regionTrendsToRankings(allRegionTrends.slice(0, 10))}
            title="Top 10 Regions by Attendance"
            height={400}
            horizontal={true}
            colorByPerformance={true}
          />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <ComparisonBarChart
            data={regionTrendsToRankings(allRegionTrends.slice(-10).reverse())}
            title="Bottom 10 Regions by Attendance"
            height={400}
            horizontal={true}
            colorByPerformance={true}
          />
        </div>
      </div>

      {/* Full Ranking Table */}
      <RankingTable
        data={regionTrendsToRankings(allRegionTrends)}
        title="All Regions Ranking"
        limit={50}
        highlightTop={5}
        highlightBottom={5}
      />
    </div>
  );
};

export default React.memo(RegionsTab);
